import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    CreateProduct as CreateProductMutation,
    CreateProductVariables as CreateProductVariables,
    ProductCreateInput,
} from 'nutshell-graphql-types';

import {bustManagedCollectionCache} from '../../../../../util/bust-managed-collection-cache';
import GET_PRODUCT_CATEGORIES from '../../../../categories/graphql/queries/get-product-categories.graphql';
import GET_PRODUCTS_SIDEBAR_COUNTS from '../../../../categories/graphql/queries/get-products-sidebar-counts.graphql';

import CREATE_PRODUCT from '../mutations/create-product.graphql';
import GET_PRODUCTS from '../queries/get-products.graphql';

import {GET_PRODUCTS_LIMIT} from './use-get-products';

export const useCreateProduct = (
    selectedCategoryId?: string | null,
    archived?: boolean | null
): {
    createProduct: (arg1: ProductCreateInput) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [createProduct, {loading, error}] = useMutation<
        CreateProductMutation,
        CreateProductVariables
    >(CREATE_PRODUCT);

    return {
        createProduct: (input: ProductCreateInput) => {
            return createProduct({
                variables: {input},
                refetchQueries: [
                    {query: GET_PRODUCTS_SIDEBAR_COUNTS},
                    {query: GET_PRODUCT_CATEGORIES},
                ],
                update: (proxy, {data}) => {
                    const product = data && data.productCreate && data.productCreate.product;

                    if (!product) return;

                    const products = proxy.readQuery({
                        query: GET_PRODUCTS,
                        variables: {
                            first: GET_PRODUCTS_LIMIT,
                            categoryId: selectedCategoryId,
                            archived,
                        },
                    });

                    if (!products) return;

                    const newEdges = [
                        // @ts-expect-error
                        ...products.products.edges,
                        {
                            node: {...product, __typename: 'Product'},
                            __typename: 'ProductEdge',
                        },
                    ];

                    proxy.writeQuery({
                        query: GET_PRODUCTS,
                        variables: {
                            first: GET_PRODUCTS_LIMIT,
                            categoryId: selectedCategoryId,
                            archived,
                        },
                        data: {
                            products: {
                                edges: newEdges,
                                __typename: 'ProductsConnection',
                            },
                            __typename: 'Query',
                        },
                    });
                },
            }).then(() => {
                // Ensure REST-backed products lists refresh to include product changes
                bustManagedCollectionCache('products');
                bustManagedCollectionCache('productCategories');
            });
        },
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
    };
};
